import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Icon } from '@iconify/react';
import Sidebar from '../../components/Sidebar';
import { Button, LoadingButton } from '../../components/ui/Button';
import { useToast } from '../../components/ui';
import apiClient from '../../services/api/apiClient';

function SettingsPage() {
  const { theme } = useTheme();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    reviewReminders: true,
    publishUpdates: false,
    systemUpdates: true
  });

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('新密碼與確認密碼不符');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('密碼長度至少需要 6 個字元');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response.data.success) {
        toast.success('密碼已成功變更');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        toast.error('密碼變更失敗');
      }
    } catch (error) {
      console.error('Password change failed:', error);
      toast.error('發生錯誤，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationToggle = (key) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isPasswordFormValid = passwordData.currentPassword && passwordData.newPassword && passwordData.confirmPassword;

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      <Sidebar activePage="" />

      <div className="flex-1 p-10 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-2 flex items-center gap-3 text-[var(--text-primary)]">
            <Icon icon="mdi:cog-outline" className="text-blue-500" />
            Settings
          </h1>
          <p className="text-sm text-[var(--text-secondary)]">
            管理您的安全設定和通知偏好
          </p>
        </div>

        {/* Security Settings */}
        <div className="rounded-xl p-6 mb-6 bg-[var(--bg-secondary)] border border-[var(--border-color)]">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2 text-[var(--text-primary)]">
            <Icon icon="mdi:shield-outline" />
            安全設定
          </h2>

          <div className="grid gap-5">
            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium mb-2 text-[var(--text-primary)]">
                目前密碼
              </label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                placeholder="請輸入目前密碼"
                className="w-full px-3.5 py-2.5 rounded-lg text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)]"
              />
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium mb-2 text-[var(--text-primary)]">
                新密碼
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                placeholder="至少 6 個字元"
                className="w-full px-3.5 py-2.5 rounded-lg text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)]"
              />
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium mb-2 text-[var(--text-primary)]">
                確認新密碼
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder="再次輸入新密碼"
                className="w-full px-3.5 py-2.5 rounded-lg text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)]"
              />
            </div>
          </div>

          {/* Change Password Button */}
          <div className="mt-6">
            <LoadingButton
              variant="primary"
              size="lg"
              loading={loading}
              loadingText="變更中..."
              icon="mdi:lock-reset"
              onClick={handlePasswordChange}
              disabled={!isPasswordFormValid}
            >
              變更密碼
            </LoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function NotificationToggle({ icon, title, description, checked, onChange }) {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)]">
      <div className="flex items-center gap-3 flex-1">
        <Icon icon={icon} className="text-2xl text-[var(--text-secondary)]" />
        <div>
          <div className="text-sm font-medium mb-1 text-[var(--text-primary)]">
            {title}
          </div>
          <div className="text-[13px] text-[var(--text-secondary)]">
            {description}
          </div>
        </div>
      </div>
      <label className="flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="hidden"
        />
        <div
          className={`w-12 h-[26px] rounded-full relative transition-colors ${
            checked ? 'bg-blue-500' : 'bg-[var(--border-color)]'
          }`}
        >
          <div
            className={`w-[22px] h-[22px] bg-white rounded-full absolute top-[2px] transition-all ${
              checked ? 'left-6' : 'left-[2px]'
            }`}
          />
        </div>
      </label>
    </div>
  );
}

function SettingItem({ icon, title, description, action }) {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)]">
      <div className="flex items-center gap-3 flex-1">
        <Icon icon={icon} className="text-2xl text-[var(--text-secondary)]" />
        <div>
          <div className="text-sm font-medium mb-1 text-[var(--text-primary)]">
            {title}
          </div>
          <div className="text-[13px] text-[var(--text-secondary)]">
            {description}
          </div>
        </div>
      </div>
      {action}
    </div>
  );
}

export default SettingsPage;
